import { supabase } from './supabase';

export interface TrackingEvent {
  status: string;
  description: string;
  location: string | null;
  timestamp: string;
}

export interface TrackingResult {
  trackingCode: string;
  deliveryStatus: string;
  estimatedDelivery: string | null;
  latestEvent: string | null;
  location: string | null;
  eventCount: number;
  events: TrackingEvent[];
  rawStatusCode?: string;
  rawStatusDescription?: string;
  rawPhaseCode?: string;
  error?: string;
}

export interface TrackedPackage {
  id: string;
  order_number: string;
  customer_name: string;
  tracking_code: string;
  carrier: string;
  delivery_status: string;
  fulfillment_status: string | null;
  shipping_address: any;
  order_date: string;
  fulfilled_at: string | null;
  last_tracking_update: string | null;
  estimated_delivery: string | null;
  total_price: number;
  currency: string;
  line_items: any;
  tracking_url: string | null;
  is_estimated: boolean;
  service_type: string | null;
  product_code: string | null;
}

export interface TrackingAnalytics {
  total_tracked: number;
  delivered_count: number;
  delivered_pct: number;
  in_transit_count: number;
  in_transit_pct: number;
  out_for_delivery_count: number;
  out_for_delivery_pct: number;
  pending_count: number;
  pending_pct: number;
  failed_count: number;
  failed_pct: number;
  returned_count: number;
  returned_pct: number;
  unconfirmed_count: number;
  unconfirmed_pct: number;
  no_info_count: number;
  no_info_pct: number;
  estimated_count: number;
  confirmed_count: number;
}

export interface PackagesResponse {
  packages: TrackedPackage[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export async function fetchTrackedPackages(
  adminId: string,
  options: {
    search?: string;
    statusFilter?: string;
    sortBy?: string;
    sortDir?: string;
    page?: number;
    pageSize?: number;
    daysBack?: number;
  } = {}
): Promise<PackagesResponse> {
  const { data, error } = await supabase.rpc('get_tracked_packages', {
    p_admin_id: adminId,
    p_search: options.search || '',
    p_status_filter: options.statusFilter || 'all',
    p_sort_by: options.sortBy || 'order_date',
    p_sort_dir: options.sortDir || 'desc',
    p_page: options.page || 1,
    p_page_size: options.pageSize || 50,
    p_days_back: options.daysBack ?? 60,
  });

  if (error) throw error;

  return {
    packages: data?.packages || [],
    total: data?.total || 0,
    page: data?.page || 1,
    page_size: data?.page_size || 50,
    total_pages: data?.total_pages || 0,
  };
}

export async function fetchTrackingAnalytics(adminId: string, daysBack: number = 60): Promise<TrackingAnalytics> {
  const { data, error } = await supabase.rpc('get_tracking_analytics', {
    p_admin_id: adminId,
    p_days_back: daysBack,
  });

  if (error) throw error;

  return data as TrackingAnalytics;
}

export async function fetchSingleTrackingStatus(
  trackingCode: string,
  adminId: string
): Promise<TrackingResult> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/postnl-batch-track`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        trackingCodes: [trackingCode],
        adminId,
      }),
      signal: controller.signal,
    }
  );
  clearTimeout(timeoutId);

  if (!response.ok) {
    throw new Error(`API returned ${response.status}`);
  }

  let data;
  try {
    data = await response.json();
  } catch {
    throw new Error('Invalid response from tracking API');
  }
  if (!data.success || !data.results?.length) {
    throw new Error(data.error || 'No tracking data returned');
  }

  return data.results[0];
}

export async function batchFetchTrackingStatus(
  trackingCodes: string[],
  adminId: string,
  onProgress?: (completed: number, total: number) => void
): Promise<TrackingResult[]> {
  const BATCH_SIZE = 5;
  const DELAY_MS = 1500;
  const MAX_RETRIES = 3;
  const allResults: TrackingResult[] = [];

  for (let i = 0; i < trackingCodes.length; i += BATCH_SIZE) {
    const batch = trackingCodes.slice(i, i + BATCH_SIZE);
    let success = false;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      if (attempt > 0) {
        const backoff = 1000 * Math.pow(2, attempt - 1);
        await new Promise((r) => setTimeout(r, backoff));
      }

      try {
        const batchCtrl = new AbortController();
        const batchTimeout = setTimeout(() => batchCtrl.abort(), 30000);
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/postnl-batch-track`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({ trackingCodes: batch, adminId }),
            signal: batchCtrl.signal,
          }
        );
        clearTimeout(batchTimeout);

        if (response.ok) {
          const data = await response.json();
          if (data.results) {
            allResults.push(...data.results);
          }
          success = true;
          break;
        }
      } catch {
        // Will retry
      }
    }

    if (!success) {
      batch.forEach((code) =>
        allResults.push({
          trackingCode: code,
          deliveryStatus: 'no_info',
          estimatedDelivery: null,
          latestEvent: null,
          location: null,
          eventCount: 0,
          events: [],
          error: 'Batch request failed after retries',
        })
      );
    }

    onProgress?.(Math.min(i + BATCH_SIZE, trackingCodes.length), trackingCodes.length);

    if (i + BATCH_SIZE < trackingCodes.length) {
      await new Promise((resolve) => setTimeout(resolve, DELAY_MS));
    }
  }

  return allResults;
}

const MAX_REFRESH_PACKAGES = 200;

export async function refreshAllActivePackages(
  adminId: string,
  onProgress?: (completed: number, total: number) => void
): Promise<{ updated: number; total: number }> {
  const { data, error } = await supabase.rpc('get_active_tracking_codes', {
    p_admin_id: adminId,
  });

  if (error) throw error;

  const codes = (Array.isArray(data) ? data : []).filter(Boolean) as string[];

  if (codes.length === 0) {
    return { updated: 0, total: 0 };
  }

  const cappedCodes = codes.slice(0, MAX_REFRESH_PACKAGES);
  const results = await batchFetchTrackingStatus(cappedCodes, adminId, onProgress);
  const successCount = results.filter((r) => !r.error).length;

  return { updated: successCount, total: cappedCodes.length };
}

export async function fetchAllPackagesForExport(
  adminId: string,
  options: { search?: string; statusFilter?: string; daysBack?: number } = {}
): Promise<TrackedPackage[]> {
  const { data, error } = await supabase.rpc('export_tracked_packages', {
    p_admin_id: adminId,
    p_search: options.search || '',
    p_status_filter: options.statusFilter || 'all',
    p_days_back: options.daysBack ?? 60,
  });

  if (error) throw error;
  return (data || []) as TrackedPackage[];
}

export interface LiveTrackingProgress {
  processed: number;
  total: number;
  succeeded: number;
  failed: number;
  currentCode: string;
  startedAt: number;
}

export interface FailedTrackingCode {
  trackingCode: string;
  error: string;
}

export async function fetchAllLiveTracking(
  adminId: string,
  onProgress: (progress: LiveTrackingProgress) => void,
  signal?: AbortSignal,
  forceAll = false
): Promise<{ succeeded: number; failed: number; total: number; failedCodes: FailedTrackingCode[] }> {
  const rpcName = forceAll ? 'get_all_tracking_codes' : 'get_active_tracking_codes';
  const { data, error } = await supabase.rpc(rpcName, {
    p_admin_id: adminId,
  });

  if (error) throw error;

  const codes = (Array.isArray(data) ? data : []).filter(Boolean) as string[];
  if (codes.length === 0) return { succeeded: 0, failed: 0, total: 0, failedCodes: [] };

  const BATCH_SIZE = 5;
  const DELAY_MS = 1500;
  const MAX_RETRIES = 3;
  let succeeded = 0;
  let failed = 0;
  const failedCodes: FailedTrackingCode[] = [];
  const startedAt = Date.now();

  for (let i = 0; i < codes.length; i += BATCH_SIZE) {
    if (signal?.aborted) break;

    const batch = codes.slice(i, i + BATCH_SIZE);

    onProgress({
      processed: i,
      total: codes.length,
      succeeded,
      failed,
      currentCode: batch[0],
      startedAt,
    });

    let batchSucceeded = false;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      if (signal?.aborted) break;

      if (attempt > 0) {
        const backoff = 1000 * Math.pow(2, attempt - 1);
        await new Promise((r) => setTimeout(r, backoff));
      }

      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/postnl-batch-track`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({ trackingCodes: batch, adminId }),
            signal,
          }
        );

        if (response.ok) {
          const respData = await response.json();
          if (respData.results) {
            for (const r of respData.results as TrackingResult[]) {
              if (r.error) {
                failed++;
                failedCodes.push({ trackingCode: r.trackingCode, error: r.error });
              } else {
                succeeded++;
              }
            }
          }
          batchSucceeded = true;
          break;
        }
      } catch (err) {
        if ((err as any)?.name === 'AbortError') break;
      }
    }

    if (!batchSucceeded && !signal?.aborted) {
      failed += batch.length;
      batch.forEach((code) =>
        failedCodes.push({ trackingCode: code, error: 'Batch request failed after retries' })
      );
    }

    if (i + BATCH_SIZE < codes.length && !signal?.aborted) {
      await new Promise((r) => setTimeout(r, DELAY_MS));
    }
  }

  onProgress({
    processed: signal?.aborted ? succeeded + failed : codes.length,
    total: codes.length,
    succeeded,
    failed,
    currentCode: '',
    startedAt,
  });

  return { succeeded, failed, total: codes.length, failedCodes };
}

export async function retryFailedTrackingCodes(
  adminId: string,
  trackingCodes: string[],
  onProgress: (progress: LiveTrackingProgress) => void,
  signal?: AbortSignal
): Promise<{ succeeded: number; failed: number; total: number; failedCodes: FailedTrackingCode[] }> {
  return fetchAllLiveTrackingForCodes(adminId, trackingCodes, onProgress, signal);
}

async function fetchAllLiveTrackingForCodes(
  adminId: string,
  codes: string[],
  onProgress: (progress: LiveTrackingProgress) => void,
  signal?: AbortSignal
): Promise<{ succeeded: number; failed: number; total: number; failedCodes: FailedTrackingCode[] }> {
  if (codes.length === 0) return { succeeded: 0, failed: 0, total: 0, failedCodes: [] };

  const BATCH_SIZE = 5;
  const DELAY_MS = 1500;
  const MAX_RETRIES = 3;
  let succeeded = 0;
  let failed = 0;
  const failedCodes: FailedTrackingCode[] = [];
  const startedAt = Date.now();

  for (let i = 0; i < codes.length; i += BATCH_SIZE) {
    if (signal?.aborted) break;

    const batch = codes.slice(i, i + BATCH_SIZE);

    onProgress({
      processed: i,
      total: codes.length,
      succeeded,
      failed,
      currentCode: batch[0],
      startedAt,
    });

    let batchSucceeded = false;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      if (signal?.aborted) break;

      if (attempt > 0) {
        const backoff = 1000 * Math.pow(2, attempt - 1);
        await new Promise((r) => setTimeout(r, backoff));
      }

      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/postnl-batch-track`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({ trackingCodes: batch, adminId }),
            signal,
          }
        );

        if (response.ok) {
          const respData = await response.json();
          if (respData.results) {
            for (const r of respData.results as TrackingResult[]) {
              if (r.error) {
                failed++;
                failedCodes.push({ trackingCode: r.trackingCode, error: r.error });
              } else {
                succeeded++;
              }
            }
          }
          batchSucceeded = true;
          break;
        }
      } catch (err) {
        if ((err as any)?.name === 'AbortError') break;
      }
    }

    if (!batchSucceeded && !signal?.aborted) {
      failed += batch.length;
      batch.forEach((code) =>
        failedCodes.push({ trackingCode: code, error: 'Batch request failed after retries' })
      );
    }

    if (i + BATCH_SIZE < codes.length && !signal?.aborted) {
      await new Promise((r) => setTimeout(r, DELAY_MS));
    }
  }

  onProgress({
    processed: signal?.aborted ? succeeded + failed : codes.length,
    total: codes.length,
    succeeded,
    failed,
    currentCode: '',
    startedAt,
  });

  return { succeeded, failed, total: codes.length, failedCodes };
}

export async function fetchUnresolvedFailures(adminId: string): Promise<Array<{ tracking_code: string; error_message: string; attempt_count: number }>> {
  const { data, error } = await supabase.rpc('get_unresolved_tracking_failures', {
    p_admin_id: adminId,
  });
  if (error) return [];
  return (data || []) as Array<{ tracking_code: string; error_message: string; attempt_count: number }>;
}

export interface LastSyncInfo {
  run_at: string;
  packages_checked: number;
  packages_updated: number;
  errors: number;
  duration_ms: number;
}

export async function fetchLastSyncInfo(adminId: string): Promise<LastSyncInfo | null> {
  if (!adminId) return null;
  const { data, error } = await supabase.rpc('get_last_postnl_sync', { p_admin_id: adminId });
  if (error || !data) return null;
  return data as LastSyncInfo;
}

export function getPostNLTrackingUrl(trackingCode: string, shippingAddress?: any): string {
  const postalCode = shippingAddress?.zip || shippingAddress?.postal_code || '';
  const countryCode = shippingAddress?.country_code || shippingAddress?.country || 'NL';
  return `https://postnl.nl/tracktrace/?B=${encodeURIComponent(trackingCode)}&P=${encodeURIComponent(postalCode)}&D=${encodeURIComponent(countryCode)}&T=C`;
}
